import {isOpportunityScene} from '@/lib/opportunityDesign';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trophy, ChevronRight, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { RewardFXProvider, useRewardFX } from './RewardFX';

const Context = createContext({ career: null, refresh: () => {} });
export const useCareer = () => useContext(Context);
export const sourceLabel = { global: 'Global', cold_call: 'Cold Call', promo: 'Promo', personal_contact: 'Meine Kontakte', referral: 'Empfehlungen' };
export const number = (v) => Number(v || 0).toLocaleString('de-DE', { maximumFractionDigits: 2 });

export function GamificationProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [career, setCareer] = useState(null);
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const reminded = useRef(null);
  const operational = ['mitarbeiter', 'teamleiter', 'vertriebspartner', 'promoter_internal'].includes(user?.base_role || user?.role);
  const needsCareer = ['/m/career','/m/home','/m/my-contacts'].includes(location.pathname) || isOpportunityScene(location);
  const refreshCareer = useCallback(async () => {
    if (!operational) return;
    const current = generation.current;
    try {
      const c = await api.get('/gamification/career', { timeout: 10000 });
      if (current !== generation.current) return;
      setCareer(c.data); setError('');
    } catch (_) { if (current === generation.current) setError('Karriere konnte nicht geladen werden.'); }
  }, [operational]);
  const refreshNotices = useCallback(async () => {
    if (!operational) return;
    const current = generation.current;
    try { const {data}=await api.get('/gamification/notices',{timeout:10000});if(current===generation.current)setNotices(data); }
    catch (_) { /* a missed poll is retried; never hide an already visible notice */ }
  },[operational]);
  const refresh = useCallback(async()=>{await Promise.all([needsCareer?refreshCareer():Promise.resolve(),refreshNotices()]);},[needsCareer,refreshCareer,refreshNotices]);
  useEffect(() => {
    generation.current += 1; setCareer(null); setNotices([]);
    if (!operational) return;
    if(needsCareer)refreshCareer();refreshNotices();
    const reminderKey=`gamification:login-reminders:${user?.id}`;
    if(reminded.current!==user?.id&&!sessionStorage.getItem(reminderKey)){
      reminded.current=user?.id;sessionStorage.setItem(reminderKey,'1');
      api.post('/gamification/login-reminders').then(refreshNotices).catch(() => { sessionStorage.removeItem(reminderKey); });
    }
    const visible=()=>{if(!document.hidden)refreshNotices();};
    const timer = setInterval(visible, 60000);
    const onChange = () => {refreshNotices();if(needsCareer)refreshCareer();};
    window.addEventListener('gamification:changed', onChange);
    return () => { generation.current += 1; clearInterval(timer); window.removeEventListener('gamification:changed', onChange); };
  }, [user?.id, operational, needsCareer, refreshCareer, refreshNotices]);
  const acknowledge = useCallback(async (id) => {
    await api.post(`/gamification/notices/${id}/ack`);
    setNotices((items) => items.filter((item) => item.id !== id));
  }, []);
  return <Context.Provider value={{ career, refresh, error }}>
    <RewardFXProvider userId={user?.id} enabled={operational && !user?.admin_mode}>
      {children}
      {operational && <AchievementDialog notice={notices[0]} acknowledge={acknowledge} />}
    </RewardFXProvider>
  </Context.Provider>;
}

export function CareerCompact() {
  const { career, error, refresh } = useCareer();
  if (!career) return error ? <button onClick={refresh} className="text-xs text-slate-400">Karriere erneut laden</button> : null;
  return <Link to="/m/career" className="block rounded-xl border border-amber-500/15 bg-amber-500/[.03] px-3 py-2 my-3" data-testid="career-compact">
    <div className="flex items-center justify-between gap-2 text-xs"><span className="text-amber-200 font-semibold">Level {career.level} · {career.stage} {career.prestige > 0 && `· Prestige ${career.prestige}`}</span><ChevronRight size={14}/></div>
    <div role="progressbar" aria-label="Fortschritt zum nächsten Level" aria-valuenow={Math.round(career.progress * 100)} aria-valuemin={0} aria-valuemax={100} className="h-1 my-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${career.progress * 100}%` }}/></div>
    {(career.next_milestones || []).map((m) => <div key={m.key} className="text-[11px] text-slate-400 truncate">{sourceLabel[m.source]} · {m.threshold} {m.label}: <span className="text-slate-200">noch {m.missing}</span></div>)}
  </Link>;
}

function AchievementDialog({ notice, acknowledge }) {
  const { busy: rewardBusy } = useRewardFX();
  const [visible, setVisible] = useState(false);
  const [media, setMedia] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);
  const dismissed = useRef(null);
  const location = useLocation();
  const noticeId = notice?.id;
  const noticeKind = notice?.kind;
  useEffect(() => {
    setVisible(false); setError('');
    if (!noticeId) return;
    const check = () => {
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      const dialog = [...document.querySelectorAll('[role="dialog"], [role="alertdialog"]')].some((el) => !el.hasAttribute('data-career-dialog'));
      if (!rewardBusy && !editing && !dialog && dismissed.current !== noticeId) setVisible(true);
    };
    check(); const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [noticeId, location.pathname, rewardBusy]);
  useEffect(() => {
    setMedia(null); if (!noticeId || noticeKind === 'reminder') return;
    let active = true, url;
    api.get(`/gamification/animation/${noticeKind}`, { responseType: 'blob' }).then(({ data }) => {
      if (!active) return;
      url = URL.createObjectURL(data); setMedia({ url, mime: data.type });
    }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [noticeId, noticeKind]);
  const close = useCallback(async () => {
    if (!noticeId || busy) return;
    setBusy(true);
    dismissed.current = noticeId;
    // Hide after at most 15 seconds even if offline; failed acknowledgement can be retried.
    setVisible(false);
    try { await acknowledge(noticeId); setError(''); }
    catch (_) { setError('Hinweis konnte noch nicht bestätigt werden.'); }
    finally { setBusy(false); }
  }, [noticeId, busy, acknowledge]);
  useEffect(() => {
    if (!visible || !noticeId) return;
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const timer = setTimeout(close, 15000);
    const key = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', key);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', key); if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [visible, noticeId, close]);
  if (!notice || !visible) return error ? <button onClick={close} className="fixed bottom-24 right-4 text-xs rounded-lg bg-slate-900 p-3 z-40">{error} Erneut versuchen</button> : null;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-5">
    <section role="dialog" aria-modal="true" aria-labelledby="career-dialog-title" data-career-dialog className="relative w-full max-w-sm rounded-2xl border border-amber-400/40 bg-[#0a1020] p-6 shadow-2xl text-center">
      <button ref={closeRef} onClick={close} aria-label="Hinweis schließen" className="absolute right-3 top-3 p-2 text-slate-300"><X size={20}/></button>
      {media && (!reduced || media.mime.startsWith('image/')) ? media.mime.startsWith('video/') ? <video src={media.url} muted autoPlay playsInline className="max-h-48 mx-auto rounded-lg"/> : <img src={media.url} alt="" className="max-h-48 mx-auto rounded-lg"/> : <Trophy className="mx-auto h-12 w-12 text-amber-300 my-3"/>}
      <h2 id="career-dialog-title" className="text-xl text-amber-100 font-semibold mt-3">{notice.title}</h2>
      {notice.detail?.message && <p className="text-sm text-slate-300 mt-3">{notice.detail.message}</p>}
      {notice.detail?.opportunity_id && <Link onClick={close} className="block text-amber-300 mt-3" to={`/m/opportunities/${notice.detail.opportunity_id}`}>Chance öffnen</Link>}
      <button onClick={close} className="gold-btn rounded-xl px-5 py-2 mt-5">Weiter</button>
      <p className="text-[10px] text-slate-500 mt-3">Schließt automatisch nach 15 Sekunden</p>
    </section>
  </div>;
}
